/* PMT nav (NSL-004 v2) — <pmt-nav> header/drawer/panels behaviour.
   Self-contained: no dependency on theme globals. Listeners are registered
   against an AbortController and torn down in disconnectedCallback, so
   shopify:section:load re-renders never duplicate wiring and no scroll-lock
   or inert state can strand. Cart count refreshes from Dawn's pub/sub bus
   when those events fire, plus a passive fetch watcher for cart mutations
   made outside the theme (apps own add-to-cart here — see observeCartMutations). */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var TRANSITION_MS = 250; /* pairs with --pmt-nav-duration in pmt-header.css */
  var OPEN_DELAY = 100;
  var CLOSE_DELAY = 300;
  var CART_EVENTS = ['cart:refresh', 'cart:updated'];
  var CART_MUTATION_EVENT = 'pmt:cart-mutation';
  var CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/;

  function setInert(on, sectionWrapper) {
    if (!('inert' in HTMLElement.prototype)) return;
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === sectionWrapper) return;
      var tag = child.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'LINK' || tag === 'TEMPLATE') return;
      if (on) {
        if (!child.hasAttribute('inert')) child.setAttribute('data-pmt-was-not-inert', '');
        child.setAttribute('inert', '');
      } else if (child.hasAttribute('data-pmt-was-not-inert')) {
        child.removeAttribute('inert');
        child.removeAttribute('data-pmt-was-not-inert');
      }
    });
  }

  /* Add-to-cart on this store is intercepted by Zipify OCU — it cancels the
     theme's form submit and POSTs /cart/add.js itself, so Dawn's cart-update
     pub/sub never fires and no theme event tells us the cart changed. Watch
     for cart-mutating requests instead: a one-time, strictly pass-through
     window.fetch wrapper that only announces a settled mutation. It rewraps
     whatever fetch is current (apps included) and never touches requests or
     responses. pmt-pdp.js routes /cart/add through a clean iframe fetch on
     PMT templates — bypassing this chain — so it dispatches the event itself. */
  function observeCartMutations() {
    if (window.__pmtCartMutationWatch) return;
    window.__pmtCartMutationWatch = true;
    var notify = function () {
      document.dispatchEvent(new CustomEvent(CART_MUTATION_EVENT));
    };
    var prevFetch = window.fetch;
    if (typeof prevFetch !== 'function') return;
    try {
      window.fetch = function (input, init) {
        var promise = prevFetch.apply(this, arguments);
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CART_MUTATION_URL.test(url)) promise.then(notify, function () {});
        return promise;
      };
    } catch (e) {}
  }

  class PmtNav extends HTMLElement {
    connectedCallback() {
      if (this._pmtInit) return;
      this._pmtInit = true;

      this.sectionWrapper = this.closest('.shopify-section-group-header-group') || this.closest('section[id]');
      this.drawer = this.querySelector('[data-pmt-drawer]');
      this.overlay = this.querySelector('[data-pmt-overlay]');
      this.burger = this.querySelector('[data-pmt-nav-burger]');
      this._lastFocus = null;
      this._timers = {};
      this._overlayHideTimer = null;
      this._drawerHideTimer = null;
      this._unsubCart = null;
      this._ac = new AbortController();
      var signal = this._ac.signal;

      this.initStickyOffset(signal);
      this.initPanels(signal);
      this.initDrawer(signal);
      this.initSearchTriggers(signal);
      this.initCart(signal);
      this.initEditorEvents(signal);
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      Object.keys(this._timers).forEach(
        function (key) {
          clearTimeout(this._timers[key]);
        }.bind(this)
      );
      if (this._overlayHideTimer) clearTimeout(this._overlayHideTimer);
      if (this._drawerHideTimer) clearTimeout(this._drawerHideTimer);
      if (this._cartDebounce) clearTimeout(this._cartDebounce);
      if (this._unsubCart) this._unsubCart();
      if (this._stickyResize) this._stickyResize.disconnect();
      if (this._stickyMutations) this._stickyMutations.disconnect();
      if (this._stickyState) this._stickyState.disconnect();
      if (this._stickyFrame) cancelAnimationFrame(this._stickyFrame);
      this._stickyFrame = null;
      if (this.drawerOpen()) this.closeDrawerNow();
      /* allow connectedCallback to re-wire if the same node is reattached
         (e.g. a theme editor section move) after the abort above */
      this._pmtInit = false;
    }

    /* Reserve only the announcement stack that actually exists. The optional
       language prompt can be omitted server-side; language cookies and leftover
       promotion-app nodes are not evidence that either bar is visible. */
    initStickyOffset(signal) {
      var selector = '.section-pmt-announcement-bar, .section-pmt-announcement-bar-v2, #epb_container, #epb_background';
      var schedule = function () {
        if (this._stickyFrame) return;
        this._stickyFrame = requestAnimationFrame(function () {
          this._stickyFrame = null;
          this.updateStickyOffset();
        }.bind(this));
      }.bind(this);
      var observe = function () {
        if (this._stickyResize) this._stickyResize.disconnect();
        if (this._stickyState) this._stickyState.disconnect();
        document.querySelectorAll(selector).forEach(function (element) {
          if (this._stickyResize) this._stickyResize.observe(element);
          if (this._stickyState) {
            this._stickyState.observe(element, { attributes: true, subtree: true, attributeFilter: ['class', 'style', 'hidden'] });
            for (var parent = element.parentElement; parent; parent = parent.parentElement) {
              this._stickyState.observe(parent, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
            }
          }
        }.bind(this));
        schedule();
      }.bind(this);
      if ('ResizeObserver' in window) this._stickyResize = new ResizeObserver(schedule);
      if ('MutationObserver' in window) {
        this._stickyState = new MutationObserver(schedule);
        this._stickyMutations = new MutationObserver(function (records) {
          var changed = records.some(function (record) {
            return Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(function (node) {
              return node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector));
            });
          });
          if (changed) observe();
        });
        this._stickyMutations.observe(document.body, { childList: true, subtree: true });
      }
      window.addEventListener('resize', schedule, { passive: true, signal });
      window.addEventListener('pageshow', schedule, { signal });
      window.addEventListener('scroll', function () {
        if (this._stickyHasPromo) schedule();
      }.bind(this), { passive: true, signal });
      document.addEventListener('shopify:section:load', observe, { signal });
      observe();
      this.updateStickyOffset();
    }

    updateStickyOffset() {
      var root = document.documentElement;
      var visibleRect = function (element) {
        if (!element) return null;
        var rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        for (var node = element; node; node = node.parentElement) {
          var style = getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return null;
        }
        return rect;
      };
      var promoBottom = 0;
      var promos = document.querySelectorAll('#epb_container, #epb_background');
      this._stickyHasPromo = false;
      promos.forEach(function (element) {
        var rect = visibleRect(element);
        if (!rect) return;
        for (var node = element; node && node !== document.body; node = node.parentElement) {
          var position = getComputedStyle(node).position;
          if (position === 'fixed' || position === 'sticky') {
            this._stickyHasPromo = true;
            if (rect.top <= 1 && rect.bottom > 0) promoBottom = Math.max(promoBottom, rect.bottom);
            break;
          }
        }
      }.bind(this));
      var announcementHeight = 0;
      document.querySelectorAll('.section-pmt-announcement-bar, .section-pmt-announcement-bar-v2').forEach(function (element) {
        var rect = visibleRect(element);
        if (rect) announcementHeight += rect.height;
      });
      var setOffset = function (name, value) {
        var next = Math.ceil(value) + 'px';
        if (root.style.getPropertyValue(name) !== next) root.style.setProperty(name, next);
      };
      setOffset('--pmt-sticky-promo-offset', promoBottom);
      setOffset('--pmt-sticky-header-offset', promoBottom + announcementHeight);
    }

    /* ---------------- desktop mega panels (D3/T3) ---------------- */

    initPanels(signal) {
      var nav = this.querySelector('.pmt-header__nav');
      if (!nav) return;

      this.items = Array.prototype.slice.call(nav.querySelectorAll('.pmt-header__nav-item'));
      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.items.forEach(
        function (item) {
          var link = item.querySelector('[data-pmt-nav-toggle]');
          var panel = item.querySelector('[data-pmt-panel]');
          if (!link || !panel) return;

          if (canHover) {
            item.addEventListener(
              'mouseenter',
              function () {
                this.schedule(item, 'open', OPEN_DELAY);
              }.bind(this),
              { signal }
            );
            item.addEventListener(
              'mouseleave',
              function () {
                this.schedule(item, 'close', CLOSE_DELAY);
              }.bind(this),
              { signal }
            );
          }

          link.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              this.clearTimers(item);
              if (item.hasAttribute('data-pmt-open')) {
                this.closePanel(item);
              } else {
                this.openPanel(item);
              }
            }.bind(this),
            { signal }
          );

          /* Esc works from anywhere inside the item, not just the toggle */
          item.addEventListener(
            'keydown',
            function (event) {
              this.onItemKeydown(event, item);
            }.bind(this),
            { signal }
          );
        }.bind(this)
      );

      /* outside click closes any open panel */
      document.addEventListener(
        'click',
        function (event) {
          if (!nav.contains(event.target)) this.closeAllPanels();
        }.bind(this),
        { signal }
      );

      /* focus leaving the nav closes any open panel */
      nav.addEventListener(
        'focusout',
        function (event) {
          if (!nav.contains(event.relatedTarget)) this.closeAllPanels();
        }.bind(this),
        { signal }
      );
    }

    onItemKeydown(event, item) {
      var link = item.querySelector('[data-pmt-nav-toggle]');
      if (!link) return;

      if (event.key === 'Escape' && item.hasAttribute('data-pmt-open')) {
        this.clearTimers(item);
        this.closePanel(item);
        link.focus();
        return;
      }

      /* Arrow keys move focus between top-level items (enhancement — the
         natural Tab order is untouched, links stay tabIndex 0) */
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        var dir = event.key === 'ArrowRight' ? 1 : -1;
        var index = this.items.indexOf(item);
        var next = this.items[(index + dir + this.items.length) % this.items.length];
        var nextLink = next && next.querySelector('[data-pmt-nav-toggle]');
        if (nextLink) nextLink.focus();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.clearTimers(item);
        this.openPanel(item);
        var first = item.querySelector('.pmt-header__panel ' + FOCUSABLE);
        if (first) first.focus();
      }
    }

    schedule(item, action, delay) {
      var key = item.querySelector('[data-pmt-nav-toggle]').getAttribute('aria-controls');
      this.clearTimers(item);
      this._timers[key] = setTimeout(
        function () {
          delete this._timers[key];
          if (action === 'open') {
            this.openPanel(item);
          } else {
            this.closePanel(item);
          }
        }.bind(this),
        delay
      );
    }

    clearTimers(item) {
      var link = item.querySelector('[data-pmt-nav-toggle]');
      if (!link) return;
      var key = link.getAttribute('aria-controls');
      if (this._timers[key]) {
        clearTimeout(this._timers[key]);
        delete this._timers[key];
      }
    }

    openPanel(item) {
      var nav = this;
      this.items.forEach(function (other) {
        if (other !== item) nav.closePanel(other);
      });
      item.setAttribute('data-pmt-open', '');
      var link = item.querySelector('[data-pmt-nav-toggle]');
      if (link) link.setAttribute('aria-expanded', 'true');
    }

    closePanel(item) {
      item.removeAttribute('data-pmt-open');
      var link = item.querySelector('[data-pmt-nav-toggle]');
      if (link) link.setAttribute('aria-expanded', 'false');
    }

    closeAllPanels() {
      var nav = this;
      (this.items || []).forEach(function (item) {
        nav.clearTimers(item);
        nav.closePanel(item);
      });
    }

    /* ---------------- drawer (T4) ---------------- */

    initDrawer(signal) {
      if (!this.drawer) return;

      if (this.burger) {
        this.burger.addEventListener('click', this.toggleDrawer.bind(this), { signal });
      }

      Array.prototype.forEach.call(
        this.drawer.querySelectorAll('[data-pmt-drawer-close]'),
        function (btn) {
          btn.addEventListener('click', this.closeDrawer.bind(this), { signal });
        }.bind(this)
      );

      Array.prototype.forEach.call(
        this.querySelectorAll('[data-pmt-accordion]'),
        function (btn) {
          btn.addEventListener(
            'click',
            function () {
              var expanded = btn.getAttribute('aria-expanded') === 'true';
              btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
              var panel = document.getElementById(btn.getAttribute('aria-controls'));
              if (panel) panel.hidden = expanded;
            }.bind(this),
            { signal }
          );
        }.bind(this)
      );

      var languageSelect = this.querySelector('[data-pmt-language-select]');
      if (languageSelect) {
        languageSelect.addEventListener(
          'change',
          function () {
            try {
              if (window.PmtLang && typeof window.PmtLang.record === 'function') {
                window.PmtLang.record(languageSelect.value, 'sel', 'mobile_drawer');
              }
            } catch (e) {}
            if (languageSelect.form) languageSelect.form.submit();
          },
          { signal }
        );
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', this.closeDrawer.bind(this), { signal });
      }

      this.addEventListener(
        'keydown',
        function (event) {
          this.onKeydown(event);
        }.bind(this),
        { signal }
      );
    }

    drawerOpen() {
      return !!this.drawer && this.drawer.hasAttribute('data-pmt-open');
    }

    toggleDrawer() {
      if (this.drawerOpen()) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    openDrawer() {
      this._lastFocus = document.activeElement;
      if (this._overlayHideTimer) clearTimeout(this._overlayHideTimer);
      if (this._drawerHideTimer) clearTimeout(this._drawerHideTimer);
      this.drawer.setAttribute('data-pmt-open', '');
      this.drawer.removeAttribute('hidden');
      if (this.overlay) {
        this.overlay.removeAttribute('hidden');
        requestAnimationFrame(
          function () {
            requestAnimationFrame(
              function () {
                this.overlay.setAttribute('data-pmt-open', '');
              }.bind(this)
            );
          }.bind(this)
        );
      }
      this.lockScroll(true);
      setInert(true, this.sectionWrapper);
      this.syncExpanded(true);
      var first;
      if (window.innerWidth < 990 || window.matchMedia('(pointer: coarse)').matches) {
        var available = function (el) {
          return el.offsetParent !== null && !el.hidden && !el.disabled && !el.hasAttribute('disabled');
        };
        first = Array.prototype.find.call(this.drawer.querySelectorAll('[data-pmt-drawer-close]'), available) ||
          Array.prototype.find.call(this.drawer.querySelectorAll('.pmt-drawer__grid a[href], .pmt-drawer__quick a[href]'), available);
        if (!first) {
          this.drawer.setAttribute('tabindex', '-1');
          first = this.drawer;
        }
      } else {
        first = this.drawer.querySelector(FOCUSABLE);
      }
      if (first) first.focus();
    }

    closeDrawer() {
      if (!this.drawerOpen()) return;
      this.drawer.removeAttribute('data-pmt-open');
      if (this.overlay) {
        this.overlay.removeAttribute('data-pmt-open');
        this._overlayHideTimer = setTimeout(
          function () {
            this.overlay.setAttribute('hidden', '');
          }.bind(this),
          TRANSITION_MS
        );
      }
      this._drawerHideTimer = setTimeout(
        function () {
          this.drawer.setAttribute('hidden', '');
        }.bind(this),
        TRANSITION_MS
      );
      this.lockScroll(false);
      setInert(false, this.sectionWrapper);
      this.syncExpanded(false);
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    closeDrawerNow() {
      this.drawer.removeAttribute('data-pmt-open');
      this.drawer.setAttribute('hidden', '');
      if (this.overlay) {
        this.overlay.removeAttribute('data-pmt-open');
        this.overlay.setAttribute('hidden', '');
      }
      this.lockScroll(false);
      setInert(false, this.sectionWrapper);
      this.syncExpanded(false);
    }

    lockScroll(on) {
      var root = document.documentElement;
      if (on) {
        var scrollbar = window.innerWidth - root.clientWidth;
        if (scrollbar > 0) root.style.setProperty('--pmt-scroll-comp', scrollbar + 'px');
        root.setAttribute('data-pmt-nav-scroll-lock', '');
      } else {
        root.removeAttribute('data-pmt-nav-scroll-lock');
        root.style.removeProperty('--pmt-scroll-comp');
      }
    }

    syncExpanded(open) {
      if (this.burger) this.burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.drawerOpen()) {
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !this.drawerOpen()) return;
      var focusables = Array.prototype.filter.call(this.drawer.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------- search triggers (T5) ---------------- */

    initSearchTriggers(signal) {
      Array.prototype.forEach.call(
        this.querySelectorAll('[data-pmt-search-open]'),
        function (btn) {
          btn.addEventListener(
            'click',
            function () {
              document.dispatchEvent(
                new CustomEvent('pmt-search:open', {
                  detail: { trigger: btn },
                  bubbles: true
                })
              );
            },
            { signal }
          );
        }.bind(this)
      );
    }

    /* ---------------- cart count (D10) ---------------- */

    initCart(signal) {
      if (!this.querySelector('[data-pmt-cart-count]')) return;

      var refresh = this.refreshCartCount.bind(this);

      /* constants.js declares PUB_SUB_EVENTS with top-level const — a global
         lexical binding that is NOT a window property — so it must be read
         as a bare identifier, guarded for contexts where it hasn't loaded */
      var cartUpdateEvent = 'cart-update';
      try {
        if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
          cartUpdateEvent = PUB_SUB_EVENTS.cartUpdate;
        }
      } catch (e) {
        /* identifier unavailable — keep the default event name */
      }

      if (typeof subscribe === 'function') {
        this._unsubCart = subscribe(cartUpdateEvent, refresh);
      }
      CART_EVENTS.forEach(function (name) {
        window.addEventListener(
          name,
          function (event) {
            refresh(event && event.detail);
          },
          { signal }
        );
      });
      document.addEventListener(CART_MUTATION_EVENT, refresh, { signal });
      window.addEventListener('pageshow', refresh, { signal });
    }

    refreshCartCount(eventData) {
      /* cart-update events carry the post-change cart — trust it and skip
         the extra request */
      var cartData = eventData && eventData.cartData;
      if (cartData && typeof cartData.item_count === 'number') {
        if (this._cartDebounce) clearTimeout(this._cartDebounce);
        this.updateCount(cartData.item_count);
        return;
      }

      if (this._cartDebounce) clearTimeout(this._cartDebounce);
      var component = this;
      this._cartDebounce = setTimeout(function () {
        /* no-store: /cart.js ships no browser-facing Cache-Control and iOS
           Safari will happily answer from a heuristically cached copy */
        fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            component.updateCount(cart.item_count);
          })
          .catch(function () {});
      }, 150);
    }

    updateCount(count) {
      if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) return;
      Array.prototype.forEach.call(this.querySelectorAll('[data-pmt-cart-count]'), function (bubble) {
        bubble.hidden = count === 0;
        var numeral = bubble.querySelector('[aria-hidden="true"]');
        if (numeral) numeral.textContent = count < 100 ? String(count) : '';
        var label = bubble.querySelector('.pmt-nav-sr-only');
        if (label) {
          var template = bubble.getAttribute(count === 1 ? 'data-count-template-one' : 'data-count-template-other') || bubble.getAttribute('data-count-template');
          label.textContent = template ? template.replace('[count]', String(count)) : String(count);
        }
      });
    }

    /* ---------------- theme editor events (T10) ---------------- */

    initEditorEvents(signal) {
      document.addEventListener(
        'shopify:block:select',
        function (event) {
          if (!this.contains(event.target)) return;
          var item = event.target.closest('.pmt-header__nav-item');
          if (item && item.hasAttribute('data-pmt-open') === false) {
            if (item.querySelector('[data-pmt-panel]')) this.openPanel(item);
          }
        }.bind(this),
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        function (event) {
          if (!this.contains(event.target)) return;
          this.closeAllPanels();
        }.bind(this),
        { signal }
      );
    }
  }

  // Pause only compact vendor teasers while mobile navigation/filter controls are open.
  // The full popup stays under the vendor's control; no consent or subscription state is changed.
  function initCompactMarketingPromptGuard() {
    var frame = null;
    var frameObserver = null;
    var frameResize = null;
    var scheduled = false;
    var pausedClass = 'ns-compact-prompt-paused';
    var drawerSelector = '.pmt-nav__drawer[data-pmt-open], .mobile-facets__disclosure[open]';

    function bindFrame() {
      var next = document.getElementById('ps__widget');
      if (next === frame) return;
      if (frameObserver) frameObserver.disconnect();
      if (frameResize) frameResize.disconnect();
      if (frame) frame.classList.remove(pausedClass);
      frame = next;
      if (!frame) return;
      frameObserver = new MutationObserver(schedule);
      frameObserver.observe(frame, { attributes: true, attributeFilter: ['style', 'width', 'height'] });
      if ('ResizeObserver' in window) {
        frameResize = new ResizeObserver(schedule);
        frameResize.observe(frame);
      }
    }

    function update() {
      scheduled = false;
      bindFrame();
      if (!frame) return;
      var rect = frame.getBoundingClientRect();
      var compact = rect.height > 0 && rect.height <= 160 && rect.width > 0 && rect.width <= 480;
      var paused = window.innerWidth <= 989 && compact && !!document.querySelector(drawerSelector);
      if (frame.classList.contains(pausedClass) !== paused) frame.classList.toggle(pausedClass, paused);
    }

    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }

    var drawerObserver = new MutationObserver(schedule);
    document.querySelectorAll('.pmt-nav__drawer').forEach(function (drawer) {
      drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['data-pmt-open'] });
    });
    document.addEventListener('toggle', function (event) {
      if (event.target.matches && event.target.matches('.mobile-facets__disclosure')) schedule();
    }, true);
    window.addEventListener('resize', schedule, { passive: true });
    // Postscript inserts/replaces its iframe asynchronously. Ignore unrelated subtree changes.
    var discoveryObserver = new MutationObserver(function () {
      if (!frame || !frame.isConnected) schedule();
    });
    discoveryObserver.observe(document.body, { childList: true, subtree: true });
    schedule();
  }

  initCompactMarketingPromptGuard();

  observeCartMutations();

  if (!customElements.get('pmt-nav')) {
    customElements.define('pmt-nav', PmtNav);
  }
})();
